import React, {useEffect, useState} from 'react';

import apiClientConfig from 'configs/app';
import {useDispatch} from 'react-redux';
import {
  Money,
  ReferralUser,
  ReferralUserList as ReferralUserListModel,
} from '../../../../../api-client';
import {WithdrawRequest} from '../../../../../api-client';
import {
  AccountNotReadyWarningSection,
  Block,
  Button,
  Checkbox,
  ColoredText,
  Container,
  CopyToClipboard,
  Loader,
  Pagination,
  PrimaryButton,
  ReferralConfirmationModal,
  ReferralPayout,
  ReferralUserList,
  Section,
} from '../../../../../components';
import {PrimaryButtonColor} from '../../../../../components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import {TextColor} from '../../../../../components/common/typography/texts/ColoredText/ColoredText';
import {useMountAnimation} from '../../../../../components/pages/detailed-project/hooks';
import {Filter} from '../../../../../components/pages/transactions/TransactionsPartialFilter/TransactionsPartialFilter';
import {createReferralManager} from '../../../../../di';
import {useErrorCallback, useProfileData} from '../../../../../hooks';
import {setReferralEnabled} from '../../../../../store/profile';
import {cn, resultIf} from '../../../../../utils';
import styles from './ReferralProgram.module.scss';

const ReferralProgram = () => {
  const mounted = useMountAnimation();
  const profileData = useProfileData();
  const dispatch = useDispatch();
  const isUserNotVerified = !profileData.isVerified && !profileData.hasIdentificationDraft;
  const referralLink = profileData.paymentCode
    ? `${apiClientConfig.publicBaseUrl}/enter/?r=${profileData.paymentCode}`
    : '';

  const [confirmationUuid, setConfirmationUuid] = useState<string | undefined>(undefined);
  const [referralModalActive, setReferralModalActive] = useState(false);
  const [isRequestingCode, setIsRequestingCode] = useState(false);

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<ReferralUser[]>([]);
  const [totalAccrued, setTotalAccrued] = useState<Money>(new Money(0, 'RUB'));
  const [totalAvailable, setTotalAvailable] = useState<Money>(new Money(0, 'RUB'));
  const [withdrawals, setWithdrawals] = useState<WithdrawRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [checkedConditions, setCheckedConditions] = useState({
    loyaltyProgram: false,
    goodSharing: false,
  });

  const handleCheckboxChange = (name: keyof typeof checkedConditions) => {
    setCheckedConditions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const errorCallback = useErrorCallback();

  const handleRequestCode = async () => {
    if (isRequestingCode) return;

    setIsRequestingCode(true);
    try {
      const manager = await createReferralManager();
      const result = await manager.sendCode();
      setConfirmationUuid(result.uuid);
      setReferralModalActive(true);
    } catch (e: unknown) {
      setReferralModalActive(false);
      await errorCallback(e as Error);
    } finally {
      setIsRequestingCode(false);
    }
  };

  const loadData = async (page: number, perPage: number): Promise<void> => {
    setLoading(true);
    try {
      const filter: Filter = {
        search: '',
        dateFrom: '',
        dateTo: '',
        transactionDirection: null,
        projectUuids: [],
      };
      const manager = await createReferralManager();
      const response: ReferralUserListModel = await manager.getUserList(page, perPage, filter);
      const withdrawalRes = await manager.getWithdrawList(page, perPage);

      dispatch(setReferralEnabled(profileData?.referralEnabled ?? false));
      setUsers(response.users.items);
      setTotalAccrued(response.totalAccrued);
      setTotalAvailable(response.totalAvailable);
      setWithdrawals(withdrawalRes.items);
      setTotalCount(response.users.totalCount);
    } catch (err: any) {
      await errorCallback(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1, 25);
  }, []);

  const programDescription = (
    <div className={styles.referralProgram__info}>
      <p>Поделитесь тем, чему доверяете — растите вместе с «Кономикой»!</p>
      <p>
        Если вам близок подход «Кономики» и вы готовы рекомендовать нас — реферальная программа
        именно для вас.
      </p>
      <p>
        За каждую инвестицию, совершённую по вашей рекомендации, вы получаете реферальный доход в
        размере 1% размера участия реферала в сделках, без ограничения по срокам
      </p>
      <p>Делитесь тем, что работает — вместе сильнее.</p>
    </div>
  );

  const conditionsNote = (
    <span className={styles.referralProgram__textBottom}>
      Порядок начисления вознаграждений закреплен в условиях{' '}
      <ColoredText color={TextColor.PRIMARY_GREEN}>
        <a
          href="https://cdn.conomica.ru/file/3170a/a33/00cc-cfe28/usloviya-akcii-delimsya-xorosim.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          Акции Делимся хорошим,
        </a>
      </ColoredText>
      применяемой в рамках{' '}
      <ColoredText color={TextColor.PRIMARY_GREEN}>
        <a
          href="https://cdn.conomica.ru/file/3170a/a33/00ec-1cf31/usloviya-programmy-klientskoi-loyalnosti-onlain-servisa-konomika.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          Программы клиентской лояльности Онлайн-сервиса Кономика
        </a>
      </ColoredText>
    </span>
  );

  return (
    <Pagination
      onLoad={loadData}
      count={users.length}
      totalCount={totalCount}
      filterObject={{}}
      initialChangeFilter={() => {}}
    >
      <div className={cn(styles.referral, resultIf(mounted, styles.mounted))}>
        <Container>
          <Section className={styles.section}>
            {isUserNotVerified && <AccountNotReadyWarningSection />}

            {loading ? (
              <Loader />
            ) : (
              <div className={styles.grid}>
                <div className={styles.mainContent}>
                  <Block>
                    <Block.Content>
                      <Block.Header withPadding>
                        <div className={styles.blockHeading}>Реферальная программа</div>
                      </Block.Header>
                      {!profileData.referralEnabled ? (
                        <Block.Body className={styles.referralProgram} withPadding>
                          {programDescription}
                          {conditionsNote}
                          <div className={styles.referralProgram__conditions}>
                            <Checkbox
                              checked={checkedConditions.loyaltyProgram}
                              onChange={() => handleCheckboxChange('loyaltyProgram')}
                            >
                              <span className={styles.referralProgram__textBottom}>
                                Ознакомлен и согласен с условиями «Программы клиентской лояльности
                                Онлайн-сервиса Кономика»
                              </span>
                            </Checkbox>
                            <Checkbox
                              checked={checkedConditions.goodSharing}
                              onChange={() => handleCheckboxChange('goodSharing')}
                            >
                              <span className={styles.referralProgram__textBottom}>
                                Ознакомлен и согласен с условиями Акции «Делимся хорошим»
                              </span>
                            </Checkbox>
                          </div>
                          <PrimaryButton color={PrimaryButtonColor.GREEN} expanded large>
                            <Button
                              onClick={handleRequestCode}
                              disabled={
                                isUserNotVerified ||
                                isRequestingCode ||
                                !checkedConditions.loyaltyProgram ||
                                !checkedConditions.goodSharing
                              }
                            >
                              {isRequestingCode ? 'Отправка запроса...' : 'Запросить реферальный код'}
                            </Button>
                          </PrimaryButton>
                        </Block.Body>
                      ) : (
                        <Block.Body className={styles.referralProgram} withPadding>
                          {programDescription}
                          <div className={styles.referralProgram__linkContainer}>
                            <div className={styles.referralProgram__link}>
                              {referralLink ? referralLink : 'Код ещё не загружен'}
                            </div>
                            <CopyToClipboard textToCopy={referralLink} withDescription />
                          </div>
                          <span className={styles.referralProgram__textBottom}>
                            Ссылка действует бессрочно и рассчитана на неограниченное количество
                            приглашений.
                          </span>
                          {conditionsNote}
                        </Block.Body>
                      )}
                    </Block.Content>
                  </Block>

                  <Pagination.Content loading={loading}>
                    <ReferralUserList users={users} />
                  </Pagination.Content>
                </div>

                <div className={styles.sidebar}>
                  <ReferralPayout
                    totalAccrued={totalAccrued}
                    totalAvailable={totalAvailable}
                    withdrawals={withdrawals}
                  />
                </div>
              </div>
            )}
          </Section>

          <Pagination.Paginator />

          {confirmationUuid && (
            <ReferralConfirmationModal
              active={referralModalActive}
              onClose={() => setReferralModalActive(false)}
              confirmationUuid={confirmationUuid}
              onSuccess={() => {
                setReferralModalActive(false);
                dispatch(setReferralEnabled(true));
              }}
            />
          )}
        </Container>
      </div>
    </Pagination>
  );
};

export default ReferralProgram;
